import { useEffect, useRef, useState } from "react";

const OTP_LENGTH = 6;
const START_SECONDS = 59;

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 20,
  background: "rgba(30, 38, 53, 0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "32px 16px",
  boxSizing: "border-box",
  overflow: "auto",
};

const dialogStyle = {
  margin: "auto",
  background: "#FFFFFF",
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 4px 32px rgba(0, 0, 0, 0.06)",
  display: "flex",
  flexDirection: "column",
  fontFamily: "Rubik, sans-serif",
  color: "#1E2635",
};

const navbarStyle = {
  height: 52,
  padding: 16,
  boxSizing: "border-box",
  flexShrink: 0,
};

const sectionStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

const badgeStyle = {
  width: 72,
  height: 72,
  borderRadius: 200,
  background: "rgba(1, 121, 59, 0.05)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const titleStyle = {
  margin: 0,
  width: "100%",
  textAlign: "center",
  fontSize: 24,
  fontWeight: 500,
  lineHeight: "32px",
  color: "#1E2635",
};

const bodyStyle = {
  margin: 0,
  width: "100%",
  textAlign: "center",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "20px",
  color: "#525252",
};

const pinStyle = {
  width: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
};

const digitRowStyle = {
  display: "flex",
  justifyContent: "center",
};

const digitStyle = {
  boxSizing: "border-box",
  border: "1px solid #E1E3E4",
  borderRadius: 12,
  background: "#FFFFFF",
  textAlign: "center",
  fontFamily: "inherit",
  color: "#1E2635",
  outline: "none",
};

const reminderStyle = {
  margin: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "20px",
};

const resendStyle = {
  margin: 0,
  padding: 0,
  border: "none",
  background: "transparent",
  fontFamily: "inherit",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "20px",
  color: "#1E2635",
  textDecoration: "underline",
  textUnderlineOffset: "auto",
  cursor: "pointer",
};

const errorStyle = {
  margin: 0,
  width: "100%",
  textAlign: "center",
  fontSize: 12,
  lineHeight: "18px",
  color: "#B42318",
};

const footerStyle = {
  background: "#F9FAFA",
  borderTop: "1px solid #ECEDEE",
  boxSizing: "border-box",
};

function formatRemaining(seconds) {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")} second`;
}

export default function OtpModal({ maskedMobile, submitting, error, onVerify, onResend, hidden = false }) {
  const [digits, setDigits] = useState(() => Array(OTP_LENGTH).fill(""));
  const [secondsLeft, setSecondsLeft] = useState(START_SECONDS);
  const inputsRef = useRef([]);

  useEffect(() => {
    if (!hidden) {
      return undefined;
    }
    return () => {
      inputsRef.current[0]?.focus();
    };
  }, [hidden]);

  useEffect(() => {
    if (secondsLeft <= 0) {
      return undefined;
    }
    const timer = setInterval(() => {
      setSecondsLeft((current) => (current > 0 ? current - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const code = digits.join("");
  const complete = code.length === OTP_LENGTH && digits.every((digit) => digit !== "");
  const expired = secondsLeft <= 0;

  const focusAt = (index) => {
    const target = inputsRef.current[Math.max(0, Math.min(index, OTP_LENGTH - 1))];
    target?.focus();
    target?.select();
  };

  const writeDigits = (start, raw) => {
    const chars = raw.replace(/\D/g, "").slice(0, OTP_LENGTH - start).split("");
    if (chars.length === 0) {
      return;
    }
    setDigits((current) => {
      const next = [...current];
      chars.forEach((char, offset) => {
        next[start + offset] = char;
      });
      return next;
    });
    focusAt(start + chars.length);
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    if (!complete || submitting) {
      return;
    }
    const ok = await onVerify(code);
    if (!ok) {
      setDigits(Array(OTP_LENGTH).fill(""));
      focusAt(0);
    }
  };

  const handleResend = async () => {
    if (submitting || !expired) {
      return;
    }
    const ok = await onResend();
    if (ok) {
      setDigits(Array(OTP_LENGTH).fill(""));
      setSecondsLeft(START_SECONDS);
      focusAt(0);
    }
  };

  const phone = maskedMobile ? ` ${maskedMobile}` : "";

  return (
    <div style={hidden ? { ...overlayStyle, display: "none" } : overlayStyle} aria-hidden={hidden || undefined}>
      <style>{`
        .dd-otp-dialog {
          width: 100%;
          max-width: 328px;
        }
        .dd-otp-form {
          width: 100%;
          min-width: 0;
        }
        .dd-otp-section {
          padding: 24px 16px;
        }
        .dd-otp-digits {
          width: 100%;
          gap: 4px;
        }
        .dd-otp-digit {
          flex: 1 1 0;
          width: 100%;
          min-width: 0;
          height: 50px;
          padding: 0;
          font-size: 16px;
          font-weight: 600;
          line-height: 24px;
        }
        .dd-otp-footer {
          padding: 15px 16px 16px;
        }
        .dd-otp-digit:focus {
          border-color: #01793B;
        }
        @media (min-width: 1024px) {
          .dd-otp-dialog {
            width: 516px;
            max-width: 516px;
          }
          .dd-otp-section {
            padding: 24px 32px;
          }
          .dd-otp-digits {
            width: 468px;
            max-width: none;
            flex-shrink: 0;
            gap: 12px;
          }
          .dd-otp-digit {
            flex: 0 0 68px;
            width: 68px;
            min-width: 68px;
            max-width: 68px;
            height: 72px;
            padding: 0 15px;
            font-size: 24px;
            font-weight: 500;
            line-height: 70px;
          }
          .dd-otp-footer {
            padding: 15px 32px 16px;
          }
        }
        .dd-otp-verify {
          width: 100%;
          height: 40px;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          border-radius: 12px;
          padding: 0 16px;
          background: #01793B;
          color: #FFFFFF;
          -webkit-text-fill-color: #FFFFFF;
          opacity: 1;
          font-family: inherit;
          font-size: 14px;
          font-weight: 500;
          line-height: 20px;
          letter-spacing: 0;
          cursor: pointer;
          outline: none;
        }
        .dd-otp-verify:hover:not(:disabled) {
          background: #016531;
        }
        .dd-otp-verify:active:not(:disabled) {
          background: #013D1E;
        }
        .dd-otp-verify:focus:not(:disabled) {
          border: 2px solid rgba(1, 121, 59, 0.2);
          padding: 0 14px;
        }
        .dd-otp-verify:disabled {
          background: #C0C2C6;
          color: #9EA1A8;
          -webkit-text-fill-color: #9EA1A8;
          border: none;
          padding: 0 16px;
          cursor: default;
        }
      `}</style>
      <div className="dd-otp-dialog" style={dialogStyle} role="dialog" aria-modal="true" aria-labelledby="dd-otp-title">
        <div style={navbarStyle} />

        <form className="dd-otp-form" onSubmit={handleVerify}>
          <div className="dd-otp-section" style={sectionStyle}>
            <div style={badgeStyle}>
              <img src="/assets/images/direct-debit/check-circle.svg" alt="" width={40} height={40} style={{ width: 40, height: 40, display: "block" }} />
            </div>
            <h1 id="dd-otp-title" style={titleStyle}>Verify your card</h1>
            <p style={bodyStyle}>
              {`We have sent an OTP your registration mobile number for card verification${phone}`}
            </p>
            <div style={pinStyle}>
              <div className="dd-otp-digits" style={digitRowStyle}>
                {digits.map((digit, index) => (
                  <input
                    key={index}
                    ref={(node) => {
                      inputsRef.current[index] = node;
                    }}
                    className="dd-otp-digit"
                    style={digitStyle}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    maxLength={index === 0 ? OTP_LENGTH : 1}
                    value={digit}
                    aria-label={`Digit ${index + 1}`}
                    onChange={(event) => writeDigits(index, event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Backspace" && digits[index] === "" && index > 0) {
                        event.preventDefault();
                        setDigits((current) => {
                          const next = [...current];
                          next[index - 1] = "";
                          return next;
                        });
                        focusAt(index - 1);
                      }
                    }}
                    onFocus={(event) => event.target.select()}
                    onPaste={(event) => {
                      event.preventDefault();
                      writeDigits(index, event.clipboardData.getData("text"));
                    }}
                  />
                ))}
              </div>
              {expired ? (
                <button type="button" style={resendStyle} onClick={handleResend} disabled={submitting}>
                  Resend OTP
                </button>
              ) : (
                <p style={reminderStyle}>
                  <span style={{ color: "#505662" }}>Reminder time</span>
                  <span style={{ color: "#1E2635" }}>{formatRemaining(secondsLeft)}</span>
                </p>
              )}
              {error ? <p style={errorStyle}>{error}</p> : null}
            </div>
          </div>

          <div className="dd-otp-footer" style={footerStyle}>
            <button className="dd-otp-verify" type="submit" disabled={!complete || submitting}>
              Verify
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
